/**
 * Utilities for resolving which Home a user receives and which
 * employee widgets are visible based on licensing and permissions.
 */

import {
  SecurityRolePermission,
  hasModuleViewPermission,
} from 'tests/services/permissionService';

export type HomePageVariant = 'manager' | 'employee';

export interface SecurityRoleTypeRef {
  label?: string;
}

export interface SecurityRoleHomePageInput {
  securityRoleType?: SecurityRoleTypeRef | null;
}

export interface EmployeeWidgetVisibility {
  canViewSchedule: boolean;
  canViewBroadcasts: boolean;
  canViewChecklist: boolean;
  canViewTodos: boolean;
}

const EMPLOYEE_LABEL_PATTERN = /employee/i;
const MANAGER_LABEL_PATTERN = /manager/i;

/**
 * Resolves the Home variant from the security role's notification level.
 */
export function resolveHomePageVariant(
  role: SecurityRoleHomePageInput | null | undefined,
): HomePageVariant {
  const notificationLabel = role?.securityRoleType?.label ?? '';
  if (EMPLOYEE_LABEL_PATTERN.test(notificationLabel)) return 'employee';
  if (MANAGER_LABEL_PATTERN.test(notificationLabel)) return 'manager';
  return 'manager';
}

export function isProductLicensed(
  products: Array<{ label: string }>,
  productLabel: string,
): boolean {
  if (!products.length) return true;
  return products.some((product) => product.label === productLabel);
}

/**
 * Determines which employee Home widgets are visible.
 * A widget requires both a licensed product and a matching view permission.
 */
export function resolveEmployeeWidgetVisibility(
  perms: SecurityRolePermission[],
  products: Array<{ label: string }>,
): EmployeeWidgetVisibility {
  const licensed = (productLabel: string) => isProductLicensed(products, productLabel);

  const canViewSchedule =
    licensed('Scheduler') &&
    (hasModuleViewPermission(perms, 'Schedule - My') ||
      hasModuleViewPermission(perms, 'Schedule - All'));

  const canViewBroadcasts =
    licensed('Broadcast') &&
    (hasModuleViewPermission(perms, 'Broadcast - My') ||
      hasModuleViewPermission(perms, 'Broadcast - All') ||
      hasModuleViewPermission(perms, 'Broadcast - Organization'));

  const canViewChecklist =
    licensed('Checklist') &&
    (hasModuleViewPermission(perms, 'Checklist - My') ||
      hasModuleViewPermission(perms, 'Checklist - All'));

  const canViewTodos =
    licensed('TODO') &&
    (hasModuleViewPermission(perms, 'To-Do - My') ||
      hasModuleViewPermission(perms, 'To-Do - All'));

  return {
    canViewSchedule,
    canViewBroadcasts,
    canViewChecklist,
    canViewTodos,
  };
}

export function getCurrentUserIdString(): string | null {
  try {
    const idFromLS = localStorage.getItem('userId');
    if (idFromLS) return idFromLS;

    const app = localStorage.getItem('BYOD-app')
      ? JSON.parse(localStorage.getItem('BYOD-app') as string)
      : null;
    const user = app?.user;
    if (!user) return null;

    if (user.id != null) return String(user.id);

    const iri = user['@id'];
    if (typeof iri === 'string') {
      const parsed = iri.split('/').pop();
      return parsed || null;
    }
  } catch {
    // ignore
  }
  return null;
}

export function getCurrentUserId(): number | null {
  const idString = getCurrentUserIdString();
  if (!idString) return null;
  const parsed = Number(idString);
  return Number.isNaN(parsed) ? null : parsed;
}
